// feedback — the "Give feedback" panel's model, pure.
//
// The draft is view-ephemeral (un-submitted text, the chosen kind, the chips
// the person removed): it lives in the host until it is sent, and survives a
// close (Esc, ×) so nothing written is lost. The context chips are built from
// facts the client already knows (the view, the build, the browser and OS the
// shell read once, the error a box reported), and every chip shown is exactly
// what is sent: each value is clipped to the server's short-text ceiling
// BEFORE it is shown, never after.
//
// "Report this" on an error box is a request through `ReportProvider` (like
// the copy door, app/copy.ts): no provider (a test, a gate outside the shell)
// means no Report this, never a fake one.

import { createContext, useContext } from "react";
import type { ExternalError } from "./externalError.js";
import type {
    FeedbackContextItem,
    FeedbackKind,
    FeedbackReportInput,
} from "./ports/feedback.js";

// The error an error box asks to report: its code and its headline.
export type ReportRequest = {
    readonly errorId: string;
    readonly summary: string;
};

export type Report = (request: ReportRequest) => void;

const ReportContext = createContext<Report | null>(null);

export const ReportProvider = ReportContext.Provider;

export const useReport = (): Report | null => useContext(ReportContext);

type ChipKey = FeedbackContextItem["type"];

export type FeedbackDraft = {
    readonly kind: FeedbackKind;
    readonly text: string;
    readonly mayContact: boolean;
    readonly removed: ReadonlySet<ChipKey>;
    // Set by "Report this": the error the report is about.
    readonly error: ReportRequest | null;
};

export const emptyDraft: FeedbackDraft = {
    kind: "problem",
    text: "",
    mayContact: false,
    removed: new Set(),
    error: null,
};

// Opening from an error box: a Problem about that error, every chip back.
// What the person already wrote stays.
export const draftForError = (
    draft: FeedbackDraft,
    error: ReportRequest,
): FeedbackDraft => ({ ...draft, kind: "problem", removed: new Set(), error });

// What the shell knows about where the person is.
export type ClientFacts = {
    readonly view: string;
    readonly version: string;
    readonly browser: string;
    readonly os: string;
};

// The server's ceiling on one context string (CL-5, `Limits.SHORT_TEXT_MAX`).
export const CONTEXT_TEXT_MAX = 200;

// Longer than the ceiling: cut, with an ellipsis inside the ceiling. Never
// inside a surrogate pair (the server refuses an unpaired one).
const clip = (s: string): string => {
    if (s.length <= CONTEXT_TEXT_MAX) return s;
    const head = s.slice(0, CONTEXT_TEXT_MAX - 1);
    const last = head.charCodeAt(head.length - 1);
    return `${last >= 0xd800 && last <= 0xdbff ? head.slice(0, -1) : head}…`;
};

// The chips the panel shows and the report sends, in the panel's order: the
// error (when reporting one), the view, the build. Removed ones are gone.
export const contextOf = (
    draft: FeedbackDraft,
    facts: ClientFacts,
): readonly FeedbackContextItem[] => {
    const all: readonly FeedbackContextItem[] = [
        ...(draft.error === null
            ? []
            : [
                  {
                      type: "feedback_context_error" as const,
                      errorId: clip(draft.error.errorId),
                      summary: clip(draft.error.summary),
                  },
              ]),
        { type: "feedback_context_view", view: clip(facts.view) },
        {
            type: "feedback_context_client",
            version: clip(facts.version),
            browser: clip(facts.browser),
            os: clip(facts.os),
        },
    ];
    return all.filter((c) => !draft.removed.has(c.type));
};

// A report says something: text, or an attached error (the domain's
// `feedback_report_empty` rule, mirrored so Send is only offered when it
// can succeed).
export const canSend = (draft: FeedbackDraft, facts: ClientFacts): boolean =>
    draft.text.trim() !== "" ||
    contextOf(draft, facts).some((c) => c.type === "feedback_context_error");

export const reportOf = (
    draft: FeedbackDraft,
    facts: ClientFacts,
): FeedbackReportInput => ({
    kind: draft.kind,
    text: draft.text,
    context: contextOf(draft, facts),
    mayContact: draft.mayContact,
});

// Why a send failed, in the panel's words: the domain's named refusals, no
// connection, or anything else (the default).
export type SendFailure =
    | "too_long"
    | "empty"
    | "daily_limit"
    | "offline"
    | "other";

export const sendFailureOf = (e: ExternalError): SendFailure => {
    if (e._tag === "External.Unreachable") return "offline";
    if (e._tag !== "External.Refused") return "other";
    switch (e.errorType) {
        case "feedback_text_too_long":
            return "too_long";
        case "feedback_report_empty":
            return "empty";
        case "feedback_daily_limit_reached":
            return "daily_limit";
        default:
            return "other";
    }
};

// The browser and OS named by a user-agent string, as people say them
// ("Chrome 141", "macOS"). Unknown → "unknown": sent as data, never guessed.
export const browserOf = (ua: string): string => {
    const known: readonly (readonly [RegExp, string])[] = [
        [/Edg\/(\d+)/, "Edge"],
        [/Firefox\/(\d+)/, "Firefox"],
        [/Chrome\/(\d+)/, "Chrome"],
        [/Version\/(\d+)[^ ]* (?:Mobile\/\S+ )?Safari/, "Safari"],
    ];
    const hit = known
        .map(([re, name]) => [re.exec(ua), name] as const)
        .find(([m]) => m !== null);
    return hit === undefined || hit[0] === null
        ? "unknown"
        : `${hit[1]} ${hit[0][1]}`;
};

export const osOf = (ua: string): string =>
    /iPhone|iPad/.test(ua)
        ? "iOS"
        : /Android/.test(ua)
          ? "Android"
          : /Mac OS X|Macintosh/.test(ua)
            ? "macOS"
            : /Windows/.test(ua)
              ? "Windows"
              : /CrOS/.test(ua)
                ? "ChromeOS"
                : /Linux/.test(ua)
                  ? "Linux"
                  : "unknown";
